/**
 * Права дашбордов и отчётов по матрице 2.2 ТЗ (копия строк backend/src/core/authz/matrix.ts):
 * - «Дашборд клиники» (`dashboard`): OWNER, MED_DIRECTOR, OPS_DIRECTOR — вся клиника; DOCTOR — только свой;
 * - обращения (`dashboard_requests`): ещё FRONT_DESK; биржа (`dashboard_exchange`): ещё HR;
 * - изменение допущений, замеров «до», рассылок, решение по предложению пилота — «Реквизиты клиники» (OWNER);
 * - заявка на переброску — «Биржа» с изменением (OWNER, OPS_DIRECTOR, HR);
 * - записи приёмов по врачам (детали скрайба) — главврач (MED_DIRECTOR), собственнику — только количество (ПОРТ-01).
 * Интерфейс только скрывает недоступное — права всё равно проверяет сервер (403).
 */
import type { ClinicRole } from '@/app/session/roles';
import type { ClinicDashboard } from './types';

const CLINIC_SCOPE: ReadonlyArray<ClinicRole> = ['OWNER', 'MED_DIRECTOR', 'OPS_DIRECTOR'];
const REQUESTS: ReadonlyArray<ClinicRole> = [...CLINIC_SCOPE, 'FRONT_DESK'];
const EXCHANGE_DASHBOARD: ReadonlyArray<ClinicRole> = [...CLINIC_SCOPE, 'HR'];
const EXCHANGE_WRITE: ReadonlyArray<ClinicRole> = ['OWNER', 'OPS_DIRECTOR', 'HR'];
const NPS_ROLES: ReadonlyArray<ClinicRole> = [...CLINIC_SCOPE, 'DOCTOR'];

const has = (roles: ReadonlyArray<ClinicRole>, allowed: ReadonlyArray<ClinicRole>) => roles.some((role) => allowed.includes(role));

/** Дашборды клиники, доступные ролям, в порядке вкладок. */
export function dashboardsFor(roles: ReadonlyArray<ClinicRole>): ClinicDashboard[] {
  const result: ClinicDashboard[] = [];
  if (has(roles, CLINIC_SCOPE)) result.push('director');
  if (has(roles, REQUESTS)) result.push('assistant');
  if (has(roles, CLINIC_SCOPE)) result.push('scribe');
  if (has(roles, EXCHANGE_DASHBOARD)) result.push('exchange');
  if (has(roles, CLINIC_SCOPE)) result.push('network');
  return result;
}

/** Дашборд клиники целиком (отчёт об экономии, пилот, NPS, рассылки). */
export function canClinicReports(roles: ReadonlyArray<ClinicRole>): boolean {
  return has(roles, CLINIC_SCOPE);
}

/** Свой дашборд врача (ДШ-08). */
export function canDoctorDashboard(roles: ReadonlyArray<ClinicRole>): boolean {
  return roles.includes('DOCTOR');
}

/** Изменять допущения, замеры «до», рассылки, принять предложение пилота. */
export function canManageReports(roles: ReadonlyArray<ClinicRole>): boolean {
  return roles.includes('OWNER');
}

/** Заявка на переброску из рекомендации (ДШ-06, HR-11). */
export function canRequestTransfer(roles: ReadonlyArray<ClinicRole>): boolean {
  return has(roles, EXCHANGE_WRITE);
}

/** Детали скрайба по врачам (время до подтверждения, правки) — у главврача; остальным — только количество. */
export function canScribeDetails(roles: ReadonlyArray<ClinicRole>): boolean {
  return roles.includes('MED_DIRECTOR');
}

/** Участвует в опросе NPS (директора и врачи, ДШ-07). */
export function isNpsRespondent(roles: ReadonlyArray<ClinicRole>): boolean {
  return has(roles, NPS_ROLES);
}

/** Роли разделов для реестра. */
export const SECTION_ROLES = {
  dashboards: [...new Set<ClinicRole>([...REQUESTS, ...EXCHANGE_DASHBOARD])],
  clinicReports: [...CLINIC_SCOPE],
  doctor: ['DOCTOR'] as ClinicRole[],
  nps: [...NPS_ROLES],
};
